import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ContentRichTextEditor from '../../components/admin/ContentRichTextEditor';
import { getChinaYear } from '../../utils/date';

type Publication = {
  _id?: string;
  title: string;
  authors: string;
  authorIds?: string[];
  venue?: string;
  abstract?: string;
  content?: string;
  year?: number;
  type: 'journal' | 'patent' | 'other';
  url?: string;
  isHighlighted?: boolean;
  order?: number;
};

type MemberOption = {
  _id: string;
  name: string;
  title?: string;
  group?: string;
};

const emptyPublication: Publication = {
  title: '',
  authors: '',
  authorIds: [],
  venue: '',
  abstract: '',
  content: '',
  year: getChinaYear(),
  type: 'journal',
  url: '',
  isHighlighted: false,
  order: 0,
};

type InlineRichTextEditorProps = {
  label: string;
  value: string;
  onChange: (html: string) => void;
};

const InlineRichTextEditor: React.FC<InlineRichTextEditorProps> = ({
  label,
  value,
  onChange,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ script: 'sub' }, { script: 'super' }],
        ['clean'],
      ],
    }),
    []
  );

  const formats = ['bold', 'italic', 'underline', 'script'];

  return (
    <div>
      <label className="mb-1 block font-medium text-neutral-700">{label}</label>
      <div className="rounded-md border border-neutral-300 bg-white">
        <ReactQuill
          className="inline-richtext-editor"
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
        />
      </div>
    </div>
  );
};

const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const parseAuthorList = (text: string) =>
  text.split(/[，,]/).map((s) => s.trim()).filter(Boolean);

const buildAuthorsHtml = (
  authorIds: string[],
  membersById: Record<string, MemberOption>,
  otherAuthorsText: string
) => {
  const names = [
    ...authorIds.map((id) => membersById[id]?.name).filter(Boolean),
    ...parseAuthorList(otherAuthorsText),
  ];
  const unique = Array.from(new Set(names));
  return unique.length ? `<p>${unique.join('，')}</p>` : '';
};

const extractOtherAuthors = (
  authorsHtml: string,
  authorIds: string[],
  membersById: Record<string, MemberOption>
) => {
  const memberNames = new Set(
    authorIds.map((id) => membersById[id]?.name).filter(Boolean)
  );
  return parseAuthorList(stripHtml(authorsHtml))
    .filter((name) => !memberNames.has(name))
    .join('，');
};

const AdminPublications: React.FC = () => {
  const [items, setItems] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Publication | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [activeType, setActiveType] = useState<
    'journal' | 'patent' | 'other'
  >('journal');
  const [otherAuthors, setOtherAuthors] = useState('');

  const normalizeType = (type?: string): Publication['type'] =>
    type === 'conference' ? 'other' : (type as Publication['type']);

  const memberMap = useMemo(() => {
    const map: Record<string, MemberOption> = {};
    members.forEach((m) => {
      map[m._id] = m;
    });
    return map;
  }, [members]);

  const loadPublications = async () => {
    try {
      setLoading(true);
      const res = await api.get<Publication[]>('/publications');
      setItems(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load publications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPublications();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        setLoadingMembers(true);
        const res = await api.get<MemberOption[]>('/members');
        setMembers(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingMembers(false);
      }
    };
    loadMembers();
  }, []);

  const applyAuthorSelections = (
    authorIds: string[],
    otherAuthorsText: string
  ) => {
    setOtherAuthors(otherAuthorsText);
    setEditing((prev) =>
      prev
        ? {
            ...prev,
            authorIds,
            authors: buildAuthorsHtml(authorIds, memberMap, otherAuthorsText),
          }
        : prev
    );
  };

  const openCreate = () => {
    setOtherAuthors('');
    setEditing({ ...emptyPublication });
    setError(null);
  };

  const openEdit = (p: Publication) => {
    const authorIds = p.authorIds || [];
    setOtherAuthors(extractOtherAuthors(p.authors || '', authorIds, memberMap));
    setEditing({
      ...p,
      type: normalizeType(p.type),
      year: p.year || getChinaYear(),
      order: p.order ?? 0,
      authorIds,
      abstract: p.abstract || '',
      content: p.content || '',
    });
    setError(null);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (!editing) return;
    const target = e.target;
    const { name, value } = target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setEditing({ ...editing, [name]: target.checked });
      return;
    }

    if (name === 'year' || name === 'order') {
      const num = value === '' ? undefined : Number(value);
      setEditing({ ...editing, [name]: Number.isNaN(num) ? undefined : num });
    } else {
      setEditing({ ...editing, [name]: value });
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.title || !editing.authors) {
      setError('Please fill in title and authors.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload: Publication = {
        ...editing,
        type: normalizeType(editing.type),
        year: editing.year || undefined,
        order: editing.order ?? 0,
        authorIds: editing.authorIds || [],
        abstract: editing.abstract || '',
        content: editing.content || '',
      };

      if (editing._id) {
        await api.put(`/publications/${editing._id}`, payload);
      } else {
        await api.post('/publications', payload);
      }

      setEditing(null);
      await loadPublications();
    } catch (err: unknown) {
      console.error(err);
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setError('Session expired. Please sign in again and retry.');
      } else {
        setError('Save failed. Please try again later.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: Publication) => {
    if (!p._id) return;
    const ok = window.confirm(`Are you sure you want to delete "${p.title}"?`);
    if (!ok) return;
    try {
      await api.delete(`/publications/${p._id}`);
      await loadPublications();
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert('Delete failed. Please try again later.');
    }
  };

  const visibleItems = useMemo(
    () =>
      items.filter((p) => normalizeType(p.type) === activeType),
    [items, activeType]
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-serif text-2xl font-semibold text-neutral-900">
          Publications Management
        </h1>
        <button
          onClick={openCreate}
          className="rounded-full bg-brand-red px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-white hover:bg-red-700"
        >
          Add Record
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-600">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-subtle">
          <div className="flex items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-700">
            <span className="mr-1">Filter by type:</span>
            {[
              { key: 'journal', label: 'Journal Articles' },
              { key: 'patent', label: 'Patents' },
              { key: 'other', label: 'Other' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() =>
                  setActiveType(
                    tab.key as 'journal' | 'patent' | 'other'
                  )
                }
                className={`rounded-full px-3 py-0.5 ${
                  activeType === tab.key
                    ? 'bg-brand-red text-white'
                    : 'bg-white text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <table className="min-w-full text-left text-xs">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Year
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Type
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Title
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Authors
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Related Members
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Venue
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Featured
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Order
                </th>
                <th className="px-3 py-2 font-semibold text-neutral-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((p) => (
                <tr key={p._id} className="border-b border-neutral-100">
                  <td className="px-3 py-2 text-neutral-700">
                    {p.year ?? ''}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {normalizeType(p.type) === 'journal'
                      ? 'Journal'
                      : normalizeType(p.type) === 'patent'
                      ? 'Patent'
                      : 'Other'}
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-900">
                    <span className="line-clamp-2">{stripHtml(p.title)}</span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">{stripHtml(p.authors)}</span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">
                      {(p.authorIds || [])
                        .map((id) => memberMap[id]?.name)
                        .filter(Boolean)
                        .join('，')}
                    </span>
                  </td>
                  <td className="max-w-xs px-3 py-2 text-neutral-700">
                    <span className="line-clamp-2">{stripHtml(p.venue || '')}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {p.isHighlighted ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">
                    {p.order ?? 0}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => openEdit(p)}
                      className="mr-2 text-xs text-brand-red hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="text-xs text-neutral-500 hover:text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-4 text-center text-xs text-neutral-500"
                  >
                    No records yet. Click Add Record in the top-right corner.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-subtle">
            <h2 className="mb-4 text-sm font-semibold text-neutral-900">
              {editing._id ? 'Edit Record' : 'Add Record'}
            </h2>
            <div className="space-y-3 text-xs">
              <InlineRichTextEditor
                label="Title"
                value={editing.title}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, title: html } : prev))
                }
              />
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Authors
                </label>
                <p className="mb-2 text-[10px] text-neutral-500">
                  Select group members and add external co-authors; the display
                  field below is updated automatically.
                </p>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Group members (multi-select, shown on member profile pages)
                </label>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-neutral-200 px-2 py-2">
                  {loadingMembers ? (
                    <p className="text-[11px] text-neutral-500">Loading members...</p>
                  ) : members.length === 0 ? (
                    <p className="text-[11px] text-neutral-500">
                      No members found.
                    </p>
                  ) : (
                    members.map((m) => {
                      const checked = editing.authorIds?.includes(m._id) ?? false;
                      return (
                        <label
                          key={m._id}
                          className="flex cursor-pointer items-center gap-2 text-[11px] text-neutral-800"
                        >
                          <input
                            type="checkbox"
                            className="um-check h-3 w-3"
                            checked={checked}
                            onChange={() => {
                              if (!editing) return;
                              const current = editing.authorIds || [];
                              const exists = current.includes(m._id);
                              const nextIds = exists
                                ? current.filter((id) => id !== m._id)
                                : [...current, m._id];
                              applyAuthorSelections(nextIds, otherAuthors);
                            }}
                          />
                          <span>
                            {m.name}
                            {m.title ? `（${m.title}）` : ''}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="mt-1 text-[10px] text-neutral-500">
                  Selected members will automatically show related publications on
                  their profile pages.
                </p>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Other authors (not in the group)
                </label>
                <input
                  type="text"
                  value={otherAuthors}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!editing) return;
                    applyAuthorSelections(editing.authorIds || [], value);
                  }}
                  placeholder="e.g. John Smith, Jane Doe (comma separated)"
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
                <p className="mt-1 text-[10px] text-neutral-500">
                  Add co-authors outside the member list. Use commas or Chinese
                  commas to separate multiple names.
                </p>
              </div>
              <InlineRichTextEditor
                label="Authors (display on site)"
                value={editing.authors}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, authors: html } : prev))
                }
              />
              <p className="-mt-2 text-[10px] text-neutral-500">
                You can fine-tune formatting here (bold, subscript, etc.).
              </p>
              <InlineRichTextEditor
                label="Journal / Conference / Patent Info"
                value={editing.venue || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, venue: html } : prev))
                }
              />
              <ContentRichTextEditor
                label="Abstract (shown on publication detail page)"
                value={editing.abstract || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, abstract: html } : prev))
                }
              />
              <ContentRichTextEditor
                label="Detailed Introduction (shown below abstract on detail page)"
                value={editing.content || ''}
                onChange={(html) =>
                  setEditing((prev) => (prev ? { ...prev, content: html } : prev))
                }
              />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Year
                  </label>
                  <input
                    name="year"
                    type="number"
                    value={editing.year ?? ''}
                    onChange={handleChange}
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                  />
                </div>
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Type
                  </label>
                  <select
                    name="type"
                    value={editing.type}
                    onChange={handleChange}
                    className="um-input w-full bg-white px-2 py-1.5"
                  >
                    <option value="journal">Journal Article</option>
                    <option value="patent">Patent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block font-medium text-neutral-700">
                    Sort value
                  </label>
                  <input
                    name="order"
                    type="number"
                    value={editing.order ?? 0}
                    onChange={handleChange}
                    className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block font-medium text-neutral-700">
                  Link (DOI / Journal page)
                </label>
                <input
                  name="url"
                  value={editing.url || ''}
                  onChange={handleChange}
                  className="w-full rounded-md border border-neutral-300 px-2 py-1.5 outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1 text-xs text-neutral-800">
                  <input
                    type="checkbox"
                    name="isHighlighted"
                    checked={!!editing.isHighlighted}
                    onChange={handleChange}
                    className="um-check h-3 w-3"
                  />
                  Set as featured publication
                </label>
              </div>
            </div>
            {error && (
              <p className="mt-2 text-[11px] text-red-600">{error}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-full border border-neutral-300 px-4 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-brand-red px-4 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPublications;

